/**
 * Site domains: connected hosts (free subdomain + custom), pending purchase intents, and three ways to add one:
 * buy a new domain (domain search → purchase intent → cart; connected automatically after payment), connect a
 * domain already on Yeap (registered or DNS zone, one click), or an external domain (CNAME + check).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Empty, Input, List, Popconfirm, Space, Spin, Table, Tabs, Tag, Tooltip, Typography, message } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, LinkOutlined, ReloadOutlined, StarFilled, StarOutlined, DeleteOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import domainRegistrationService from '../../services/domainRegistrationService';
import { cartService } from '../../services/cartService';
import ResultList from '../domain-registration/components/search/ResultList';
import { PlansModal, showPlans } from './Media';
import { resultKey } from '../domain-registration/components/search/searchUtils';
import '../domain-registration/components/search/domainSearch.css';
import { QUIET } from './api';
import { CopyLine, StatusTag, listOf, toastErr, useSite } from './common';

const { Text } = Typography;
const SUGGEST_TLDS = ['com', 'net', 'io', 'co', 'shop', 'store', 'site', 'online', 'app', 'org'];

const SslTag = ({ v }) => {
    const { t } = useTranslation();
    if (!v) return null;
    if (v === 'active') return <Tag color="green" icon={<CheckCircleOutlined />}>HTTPS</Tag>;
    if (v === 'pending') return <Tag color="gold" icon={<LoadingOutlined />}>{t('Issuing certificate')}</Tag>;
    return <Tag>{t('No certificate')}</Tag>;
};

const BuyDomain = ({ api, siteName, onIntent }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const base = String(siteName || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '').slice(0, 40);
    const [q, setQ] = useState(base);
    const [rows, setRows] = useState([]);
    const [pending, setPending] = useState([]);
    const [adding, setAdding] = useState(new Set());

    const search = useCallback(async (raw) => {
        const s = String(raw || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
        if (!s) return;
        const [label, ...rest] = s.split('.');
        const tld = rest.join('.');
        const tlds = tld ? [tld, ...SUGGEST_TLDS.filter((x) => x !== tld)].slice(0, 8) : SUGGEST_TLDS;
        setRows([]);
        setPending(tlds.map((x) => `${label}.${x}`));
        try {
            const res = await domainRegistrationService.bulkCheckAvailability({ domains: [label], tlds, parallel: true });
            const list = Array.isArray(res?.data) ? res.data : listOf(res);
            const order = (r) => { const i = tlds.indexOf(String(r.tld || '').replace(/^\./, '')); return i < 0 ? 99 : i; };
            setRows([...list].sort((a, b) => order(a) - order(b)));
        } catch (e) {
            message.error(e?.error || t('Query failed. Please try again.'));
        } finally {
            setPending([]);
        }
    }, [t]);
    useEffect(() => { if (base) search(base); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const buy = async (r) => {
        const key = resultKey(r);
        setAdding((s) => new Set(s).add(key));
        try {
            try {
                await api.post('/domains/purchase-intent', { domain: r.domain, www: true }, QUIET);
            } catch (e) {
                if (e?.status !== 404) throw e; // backend not deployed yet: still let them buy
            }
            const res = await cartService.addDomain(r.domain, 'register', 1);
            if (res?.success === false) throw res;
            onIntent?.();
            navigate(userPath('/billing/cart'));
        } catch (e) {
            toastErr(e, t('Failed to add to cart'));
        } finally {
            setAdding((s) => { const n = new Set(s); n.delete(key); return n; });
        }
    };

    return (
        <div className="domain-search">
            <Input.Search value={q} onChange={(e) => setQ(e.target.value)} onSearch={search} enterButton={<SearchOutlined />} placeholder="example.com" allowClear style={{ maxWidth: 480, marginBottom: 12 }} />
            <ResultList rows={rows} pendingKeys={pending} adding={adding} onAdd={buy} actionLabel={t('Buy')} />
        </div>
    );
};

const ConnectMine = ({ api, onConnected }) => {
    const { t } = useTranslation();
    const [list, setList] = useState(null);
    const [busy, setBusy] = useState(null);
    const load = useCallback(() => {
        api.get('/domains/connectable', null, QUIET).then((d) => setList(listOf(d))).catch(() => setList([]));
    }, [api]);
    useEffect(() => { load(); }, [load]);
    const connect = async (d) => {
        setBusy(d.domain);
        try {
            await api.post('/domains/connect', { domain: d.domain, www: true });
            message.success(t('{{domain}} connected', { domain: d.domain }));
            onConnected?.();
            load();
        } catch (e) { toastErr(e); } finally { setBusy(null); }
    };
    if (list === null) return <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>;
    if (!list.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No domains on Yeap yet')} />;
    return (
        <List size="small" dataSource={list} renderItem={(d) => (
            <List.Item actions={[
                d.this_site ? <Tag color="green" key="c">{t('Connected')}</Tag>
                    : d.used_by ? <Tooltip key="u" title={d.used_by.name ? t('Used by {{name}}', { name: d.used_by.name }) : t('In use')}><Tag>{t('In use')}</Tag></Tooltip>
                        : <Button key="b" type="primary" size="small" loading={busy === d.domain} onClick={() => connect(d)}>{t('Connect')}</Button>,
            ]}>
                <Space>
                    <Text strong>{d.domain}</Text>
                    {d.registered && <Tag>{t('Registered')}</Tag>}
                    {d.dns_zone && <Tag color="blue">DNS</Tag>}
                </Space>
            </List.Item>
        )} />
    );
};

const ConnectExternal = ({ api, onAdded }) => {
    const { t } = useTranslation();
    const [host, setHost] = useState('');
    const [busy, setBusy] = useState(false);
    const add = async () => {
        const h = host.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
        if (!h) return;
        setBusy(true);
        try {
            const d = await api.post('/domains', { host: h });
            setHost('');
            onAdded?.(d);
        } catch (e) {
            if (e?.body?.code === 'plan_limit') { message.destroy(); showPlans(); message.info(e.body.error || e.error); } else toastErr(e);
        } finally { setBusy(false); }
    };
    return (
        <Space.Compact style={{ width: '100%', maxWidth: 480 }}>
            <Input value={host} onChange={(e) => setHost(e.target.value)} onPressEnter={add} placeholder="www.example.com" />
            <Button type="primary" loading={busy} onClick={add}>{t('Add')}</Button>
        </Space.Compact>
    );
};

const Domains = () => {
    const { t } = useTranslation();
    const { site, api, reload: reloadSite } = useSite();
    const [domains, setDomains] = useState(null);
    const [intents, setIntents] = useState([]);
    const [checking, setChecking] = useState(null);
    const [tab, setTab] = useState('buy');

    const load = useCallback(async () => {
        try { setDomains(listOf(await api.get('/domains'))); } catch (e) { toastErr(e); setDomains([]); }
        try { setIntents(listOf(await api.get('/domains/intents', null, QUIET)).filter((i) => ['pending', 'failed'].includes(i.status))); } catch (_) { setIntents([]); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const check = async (d) => {
        setChecking(d.id);
        try {
            const r = await api.post(`/domains/${d.id}/check`);
            setDomains((xs) => xs.map((x) => (x.id === d.id ? r : x)));
            if (r.status === 'active') message.success(t('{{host}} is connected', { host: r.host }));
            else message.warning(r.error || t('Not pointed yet'));
        } catch (e) { toastErr(e); } finally { setChecking(null); }
    };
    const primary = async (d) => {
        try { setDomains(listOf(await api.post(`/domains/${d.id}/primary`))); reloadSite(); } catch (e) { toastErr(e); }
    };
    const removeDomain = async (d) => {
        try { await api.del(`/domains/${d.id}`); load(); reloadSite(); } catch (e) { toastErr(e); }
    };
    const cancelIntent = async (i) => {
        try { await api.del(`/domains/intents/${i.id}`); load(); } catch (e) { toastErr(e); }
    };
    const refreshAll = () => { load(); reloadSite(); };

    const columns = [
        {
            title: t('Domain'), key: 'host', render: (_, d) => (
                <Space direction="vertical" size={2}>
                    <Space size={6} wrap>
                        <a href={`https://${d.host}`} target="_blank" rel="noopener noreferrer"><Text strong>{d.host}</Text></a>
                        {d.kind === 'subdomain' && <Tag>{t('Free')}</Tag>}
                        {d.is_primary && <Tag color="blue">{t('Primary', { context: 'shop' })}</Tag>}
                    </Space>
                    {d.kind === 'custom' && d.status !== 'active' && d.cname_target && (
                        <Text type="secondary" style={{ fontSize: 12 }}>CNAME <CopyLine value={d.cname_target} /></Text>
                    )}
                    {d.error && d.status !== 'active' && <Text type="danger" style={{ fontSize: 12 }}><ExclamationCircleOutlined /> {d.error}</Text>}
                </Space>
            ),
        },
        { title: t('Status'), key: 's', width: 220, render: (_, d) => <Space size={4} wrap><StatusTag value={d.status} /><SslTag v={d.ssl} /></Space> },
        {
            title: '', key: 'a', width: 150, align: 'right', render: (_, d) => (
                <Space size={0}>
                    {d.status !== 'active' && <Tooltip title={t('Check now')}><Button type="text" size="small" icon={<ReloadOutlined />} loading={checking === d.id} onClick={() => check(d)} /></Tooltip>}
                    <Tooltip title={d.is_primary ? t('Primary', { context: 'shop' }) : t('Make primary')}>
                        <Button type="text" size="small" icon={d.is_primary ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />} disabled={d.is_primary || d.status !== 'active'} onClick={() => primary(d)} />
                    </Tooltip>
                    {d.kind === 'custom' && (
                        <Popconfirm title={t('Remove {{host}}?', { host: d.host })} onConfirm={() => removeDomain(d)}>
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
            <Card title={t('Domains')} size="small" extra={<Button type="text" icon={<ReloadOutlined />} onClick={refreshAll} />}>
                {domains === null ? <Spin /> : (
                    <Table rowKey="id" size="small" pagination={false} dataSource={domains} columns={columns} scroll={{ x: 560 }} />
                )}
                {intents.map((i) => (
                    <Alert key={i.id} style={{ marginTop: 8 }} type={i.status === 'failed' ? 'error' : 'info'} showIcon icon={i.status === 'failed' ? undefined : <LoadingOutlined />}
                        message={<Space wrap><Text strong>{i.domain}</Text><Text type="secondary">{i.status === 'failed' ? t('Could not connect') : t('Connecting after purchase…')}</Text></Space>}
                        action={<Popconfirm title={t('Cancel?')} onConfirm={() => cancelIntent(i)}><Button size="small" type="text">{t('Cancel')}</Button></Popconfirm>} />
                ))}
            </Card>
            <Card size="small" title={<Space><LinkOutlined />{t('Add a domain')}</Space>}>
                <Tabs activeKey={tab} onChange={setTab} destroyInactiveTabPane items={[
                    { key: 'buy', label: t('Buy a new domain'), children: <BuyDomain api={api} siteName={site?.name} onIntent={load} /> },
                    { key: 'mine', label: t('My Yeap domains'), children: <ConnectMine api={api} onConnected={refreshAll} /> },
                    {
                        key: 'ext', label: t('Other domain'), children: (
                            <ConnectExternal api={api} onAdded={(d) => { message.success(t('Point a CNAME record to {{target}}', { target: d?.cname_target || '' })); refreshAll(); }} />
                        ),
                    },
                ]} />
            </Card>
            <PlansModal />
        </div>
    );
};

export default Domains;
